import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { CSSProperties } from 'react';
import { useAuth } from '@breath-lab/state';
import { useJourneys, JourneyWithState } from '@breath-lab/state';
import { journeyDetailPath } from '../../../routes';
import { PageMeta } from '../../seo/PageMeta';
import { JsonLd } from '../../seo/JsonLd';
import { getStaticJourneyListItems } from '../../../seo/routes';
import { SITE_BASE_URL } from '../../../constants';
import { Journey, UserJourney } from '../../../domain/models';
import { breathworkImageUrl } from '../../../domain/breathworkHelpers';
import Button from '@ui/components/Button/Button';
import MediaCard from '@ui/components/MediaCard/MediaCard';
import { logEvent } from '../../../infra/firebase/analytics';
import { AnalyticsEvent, AnalyticsParam } from '../../../domain/analytics';
import styles from './JourneysPage.module.css';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function formatRelativeDate(date: Date, t: (key: string, opts?: any) => string): string {
  const days = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (days === 0) return t('today', { ns: 'common' });
  if (days === 1) return t('yesterday', { ns: 'common' });
  return t('daysAgo', { ns: 'common', count: days });
}

function progressPercent(uj: UserJourney, journey: Journey): number {
  return Math.round((uj.completedSteps.length / journey.totalSteps) * 100);
}

function ActiveHero({ item }: { item: JourneyWithState }) {
  const { t } = useTranslation('journeys');
  const { journey, userJourney } = item;
  const uj = userJourney!;
  const stepNum = uj.currentStepIndex + 1;
  const pct = progressPercent(uj, journey);
  const lastPracticed = uj.lastPracticedAt ? formatRelativeDate(uj.lastPracticedAt, t as any) : null;
  const coverImage = journeyCoverImage(journey.id);

  const sortedSteps = [...journey.steps].sort((a, b) => a.position - b.position);
  const currentStep = sortedSteps[uj.currentStepIndex] ?? null;
  const breathworkImage = currentStep ? breathworkImageUrl(currentStep.techniques[0].breathworkId) : null;

  return (
    <div className={styles.hero}>
      {coverImage && (
        <div className={styles.heroCoverWrapper}>
          <img src={coverImage} alt={`${journey.name.en} breathwork journey`} className={styles.heroCover} />
        </div>
      )}

      <div className={styles.heroBody}>
        <span className={styles.heroEyebrow}>{t('inProgress')}</span>
        <h2 className={styles.heroName}>{journey.name.en}</h2>
        <div className={styles.heroMeta}>
          <span>{t('stepOf', { current: stepNum, total: journey.totalSteps })}{lastPracticed && <> &middot; {t('lastPracticed', { when: lastPracticed })}</>}</span>
          <span>{t('percentComplete', { pct })}</span>
        </div>
        <div className={styles.progressTrack}>
          <div className={styles.progressFill} style={{ '--progress': pct / 100 } as CSSProperties} />
        </div>

        {currentStep && (
          <div className={styles.heroNextStep}>
            {breathworkImage && (
              <img src={breathworkImage} alt={currentStep?.title.en ?? ''} className={styles.heroNextThumb} loading="lazy" />
            )}
            <div className={styles.heroNextInfo}>
              <span className={styles.heroNextLabel}>{t('upNext')}</span>
              <span className={styles.heroNextName}>{currentStep.title.en}</span>
            </div>
          </div>
        )}

        <Button as={Link} to={journeyDetailPath(journey.id)} variant="primary" size="md" className={styles.heroCta}>
          {t('continueJourney')}
        </Button>
      </div>
    </div>
  );
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const JOURNEY_COVER_IMAGES: Record<string, string> = {
  STILL_WATERS: '/journeys/still-waters.webp',
  MORNING_FIRE: '/journeys/morning-fire.webp',
  NIGHT_TIDE: '/journeys/night-tide.webp',
  THE_SHARPENED_MIND: '/journeys/sharpened-mind.webp',
  ROOTS_OF_PRANA: '/journeys/roots-of-prana.webp',
  CENTER_GROUND: '/journeys/center-ground.webp',
  QUIET_FURNACE: '/journeys/quiet-furnace.webp',
  THE_SETTLING: '/journeys/the-settling.webp',
  FIVE_DIMENSIONS_OF_PRANAYAMA: '/journeys/five-dimensions-of-pranayama.webp',
};

function journeyCoverImage(id: string): string | undefined {
  return JOURNEY_COVER_IMAGES[id];
}


export default function JourneysPage() {
  const { t } = useTranslation('journeys');
  const { user, isAuthenticated } = useAuth();
  const { journeys, error, reload, loadTemplates, freeIds, paywallEnabled } = useJourneys();
  const activeJourney = useJourneys((s) => s.activeJourney)();
  const uid = user?.uid ?? null;
  useEffect(() => {
    if (isAuthenticated && uid) {
      reload(uid);
    } else {
      loadTemplates();
    }
  }, [uid, isAuthenticated, reload, loadTemplates]);

  useEffect(() => {
    logEvent(AnalyticsEvent.screenView, { [AnalyticsParam.screenName]: 'journeys' });
    logEvent(AnalyticsEvent.viewJourneys);
  }, []);

  if (journeys.length === 0 && !error) {
    return (
      <div className={styles.page}>
        <PageMeta
          title={t('meta.title')}
          description={t('meta.description')}
          path="/journeys"
          image={`${SITE_BASE_URL}/og-journeys.png`}
        />
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTagline}>{t('pageTagline')}</h1>
          <p className={styles.pageDesc}>{t('pageDesc')}</p>
        </div>
        <div className={styles.skeletonHero}>
          <div className={styles.skeletonHeroCover} />
          <div className={styles.skeletonHeroBody} />
        </div>
        <ul className={styles.srOnly}>
          {getStaticJourneyListItems().map((item) => (
            <li key={item.url}><a href={item.url}>{item.name}</a></li>
          ))}
        </ul>
        <ul className={styles.list}>
          {Array.from({ length: 6 }).map((_, i) => (
            <li key={i} className={styles.skeletonCard} style={{ animationDelay: `${i * 80}ms` }}>
              <div className={styles.skeletonCardImage} />
              <div className={styles.skeletonCardBody} />
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.page}>
        <PageMeta
          title={t('meta.title')}
          description={t('meta.description')}
          path="/journeys"
          image={`${SITE_BASE_URL}/og-journeys.png`}
        />
        <div className={styles.errorState}>
          <p className={styles.errorText}>{t('errors.couldNotLoad')}</p>
          <Button variant="secondary" size="md" onClick={() => uid ? reload(uid) : loadTemplates()}>
            {t('tryAgain', { ns: 'common' })}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageMeta
        title={t('meta.title')}
        description={t('meta.description')}
        path="/journeys"
        image={`${SITE_BASE_URL}/og-journeys.png`}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'Breathwork Journeys',
          numberOfItems: getStaticJourneyListItems().length,
          itemListElement: getStaticJourneyListItems(),
        }}
      />
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTagline}>{t('pageTagline')}</h1>
        <p className={styles.pageDesc}>{t('pageDesc')}</p>
      </div>

      {activeJourney && <ActiveHero item={activeJourney} />}

      {activeJourney && (
        <p className={styles.sectionLabel}>{t('sectionLabel')}</p>
      )}

      <ul className={styles.list}>
        {[...journeys].sort((a, b) => (a.journey.position ?? 0) - (b.journey.position ?? 0)).map((item) => {
          const { journey } = item;
          const isActive = activeJourney?.journey.id === journey.id;
          const isPremium = isAuthenticated && paywallEnabled && !freeIds.includes(journey.id);
          const badge = isActive
            ? { label: t('inProgress'), variant: 'in-progress' as const }
            : isPremium
            ? { label: t('premiumBadge'), variant: 'premium' as const }
            : undefined;
          return (
            <li key={journey.id}>
              <MediaCard
                image={journeyCoverImage(journey.id)}
                badge={badge}
                title={journey.name.en}
                description={journey.description.en}
                meta={`${capitalise(journey.difficulty)} · ${journey.totalSteps} steps · ${journey.category}`}
                isActive={isActive}
                href={journeyDetailPath(journey.id)}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}
